import type { ApprovalRecordType } from './approvals.js';
import type { Action, Module } from './permissions.js';

/**
 * Panoda "bana düşen işler" (ADR 0156; ortak 2026-11-02): kişinin şimdi yapabileceği eylemler
 * (onay ya da iade kararı, inceleme, ödeme düzenleme, yayın) ve kişiye iade edilen kayıtlar. İş
 * saklanmaz; değiştirilemez gönderim, karar ve kayıt satırlarından her istekte türetilir.
 *
 * Türlerin sırası gösterim sırasıdır: iade edilenler, finans, satın alma, teknik, HSEQ, doküman,
 * ISM form (ADR 0156 "Gruplama ve sayaç").
 */
export const TASK_KINDS = [
  'returned',
  'ctm_decision',
  'ctm_transfer',
  'cash_review',
  'requisition_decision',
  'requisition_processing',
  'po_decision',
  'invoice_review',
  'payment_approval',
  'supplier_bank_review',
  'pms_decision',
  'fuel_adjustment_decision',
  'certificate_issuance',
  'hse_decision',
  'risk_decision',
  'document_revision',
  'ism_form_decision',
] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

/** Panonun bölümleri; grup başlığının ikonu ve "Tümünü gör" bölümden seçilir. */
export const TASK_SECTION_ORDER = [
  'returned',
  'finance',
  'purchasing',
  'technical',
  'hseq',
  'documents',
  'ism_forms',
] as const;
export type TaskSection = (typeof TASK_SECTION_ORDER)[number];

export const TASK_SECTIONS: Readonly<Record<TaskKind, TaskSection>> = {
  returned: 'returned',
  ctm_decision: 'finance',
  ctm_transfer: 'finance',
  cash_review: 'finance',
  requisition_decision: 'purchasing',
  requisition_processing: 'purchasing',
  po_decision: 'purchasing',
  invoice_review: 'purchasing',
  payment_approval: 'purchasing',
  supplier_bank_review: 'purchasing',
  pms_decision: 'technical',
  fuel_adjustment_decision: 'technical',
  certificate_issuance: 'technical',
  hse_decision: 'hseq',
  risk_decision: 'hseq',
  document_revision: 'documents',
  ism_form_decision: 'ism_forms',
};

/** Panoyu açan kişi: karar yeri ofis ya da gemi (ADR 0026 §3). */
export interface TaskViewer {
  userId: string;
  roleIds: readonly string[];
  at: 'office' | 'ship';
}

/** Yetki matrisinin sorusu (görme, onay, iade…); kapsam (filo, gemi, kendi) matristen. */
export type Permits = (module: Module, action: Action, vesselId: string | null) => boolean;

/**
 * Karar ucunun istediği izin (ADR 0156 "Yetki süzgeci"): kaydı görme ve onay ya da iade.
 * Onay ve iadenin ayrı hücreleri olabilir; ikisinden biri satırı açar, karar penceresi yetkisiz
 * düğmeyi göstermez.
 */
export function canDecideIn(permits: Permits, module: Module, vesselId: string | null): boolean {
  return (
    permits(module, 'view', vesselId) &&
    (permits(module, 'approve', vesselId) || permits(module, 'return', vesselId))
  );
}

/** Grupta gösterilen en çok satır; kalanı "+N daha" (ADR 0156). Sayılar kesindir. */
export const TASK_ROW_LIMIT = 3;

/** Bir iş satırı: kaydın kendisi ve kişinin adımına düştüğü an. */
export interface TaskItem {
  recordId: string;
  /** CTM-…, REQ-…, PO-… */
  recordNo: string | null;
  /** Kaydın kendi kısa adı; yoksa arayüz türün adını yazar. */
  title: string | null;
  vesselId: string | null;
  vesselName: string | null;
  /** Tutar küçük birimde (kuruş). */
  amount?: { minor: number; currency: string };
  /** ISO UTC: kişinin adımına düştüğü an (`state.since`); matris dışında gönderim anı. */
  since: string;
  overdue: boolean;
  /** Kaptanın kendi kaydı ofise yükseldi (ADR 0091). */
  escalated?: boolean;
  step?: number;
  /**
   * PMS: interval_change|defect|breakdown|work_record|postponement; doküman: approve|publish;
   * talep: review|rfq; HSE türü; iade akışı.
   */
  variant?: string;
  /** Cari ve banka bağlantısı için. */
  supplierId?: string;
  /** İade gerekçesi. */
  note?: string;
  /** Kaydın modülü ya da bölümü: iade akışının modülü, doküman bölümü (`ism`, `manuals`). */
  module?: string;
  /** Bağlı kayıt: iadede hedef kayıt, PMS'te iş emri. */
  targetId?: string | null;
}

export interface TaskGroup {
  kind: TaskKind;
  count: number;
  overdue: number;
  /** Sırası gelmemiş adımlar (yalnız ISM form; sayaca girmez, #2238). */
  upcoming?: number;
  items: TaskItem[];
}

/** `GET /api/office/dashboard` yanıtının `tasks` alanı (ADR 0156). */
export interface DashboardTasks {
  total: number;
  overdue: number;
  /** Bir ya da daha çok kaynak okunamadı; liste eksik olabilir. */
  partial: boolean;
  groups: TaskGroup[];
}

/** Kaynağın ürettiği ham iş. */
export interface RawTask {
  kind: TaskKind;
  item: TaskItem;
}

function byUrgency(a: TaskItem, b: TaskItem): number {
  if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
  return (
    a.since.localeCompare(b.since) ||
    (a.recordNo ?? '').localeCompare(b.recordNo ?? '') ||
    a.recordId.localeCompare(b.recordId)
  );
}

/**
 * İşleri türlere göre gruplar (ADR 0156): grup yalnız sayısı sıfırdan büyükse (ya da sırada iş
 * varsa) gelir; gruplar `TASK_KINDS` sırasında; grup içinde gecikenler önce, sonra en eski
 * (#2236); grupta en çok {@link TASK_ROW_LIMIT} satır, sayılar kesin. Aynı türün aynı kaydı
 * (ör. iki varyant) iki satırdır.
 */
export function groupTasks(
  raw: readonly RawTask[],
  options: {
    partial: boolean;
    upcoming?: Partial<Record<TaskKind, number>>;
    limit?: number;
  },
): DashboardTasks {
  const limit = options.limit ?? TASK_ROW_LIMIT;
  const groups: TaskGroup[] = [];
  for (const kind of TASK_KINDS) {
    const items = raw
      .filter((task) => task.kind === kind)
      .map((task) => task.item)
      .sort(byUrgency);
    const upcoming = options.upcoming?.[kind] ?? 0;
    if (items.length === 0 && upcoming === 0) continue;
    groups.push({
      kind,
      count: items.length,
      overdue: items.filter((i) => i.overdue).length,
      ...(upcoming > 0 ? { upcoming } : {}),
      items: items.slice(0, limit),
    });
  }
  return {
    total: groups.reduce((sum, g) => sum + g.count, 0),
    overdue: groups.reduce((sum, g) => sum + g.overdue, 0),
    partial: options.partial,
    groups,
  };
}

/**
 * Onay matrisindeki kayıt türünün ofis adımı panoda hangi gruba düşer (ADR 0156 "Kabul ölçütü").
 * `later`: Faz 1'de ofis adımı yok ya da sonraki fazda (gemi panosu, Faz 2). Yeni kayıt türü bu
 * tabloya girmeden derlenmez; varsayılan matriste ofis adımı olan tür `later` olamaz (test).
 */
export const OFFICE_STEP_TASK_KIND: Readonly<Record<ApprovalRecordType, TaskKind | 'later'>> = {
  // Gemi kayıtları: ofis adımı yok (MLC A2.3 §12, ADR 0022 §3, ADR 0051 §6, ADR 0038 §10)
  rest_hours_month: 'later',
  voyage_report: 'later',
  daily_report: 'later',
  vessel_document_revision: 'later',
  fuel_opening_balance: 'later',
  fuel_bunkering: 'later',
  fuel_movement: 'later',
  requisition: 'requisition_decision',
  document_revision: 'document_revision',
  // ADR 0073'ten sonra yalnız eski adımlar
  ism_form: 'ism_form_decision',
  // Şirket iş kaydına ya da ertelemeye ofis adımı açtıysa (PMS ofis adımı, M7b3)
  pms_work_record: 'pms_decision',
  postponement_request: 'pms_decision',
  maintenance_interval_change: 'pms_decision',
  defect_report: 'pms_decision',
  breakdown_report: 'pms_decision',
  hse_non_conformity: 'hse_decision',
  hse_deficiency: 'hse_decision',
  hse_near_miss: 'hse_decision',
  hse_accident: 'hse_decision',
  risk_assessment: 'risk_decision',
  ctm_request: 'ctm_decision',
  fuel_adjustment: 'fuel_adjustment_decision',
};
